import { action, makeObservable, observable, when } from 'mobx';
import { Constraint, generateEmailFieldConstraints } from '../utils/validators';
import { FormField } from './formField';
import { RootStore } from './root';
import { ROUTES } from '../routes';
import {
  ACCOUNT_RECOVERY_FORGOT_PASSWORD_URL,
  AUTH_METHODS_BY_MAIL,
  NEW_ACCOUNT_RECOVERY_URL,
  IAUTH_METHODS_BY_MAIL,
  LOGIN_PAGE_CONTEXT,
  SIGNUP_PAGE_CONTEXT,
  forgotPasswordParams,
  getVerticalSupportUrl,
  WIX_DEFAULT_ORGANIZATION_ID,
} from '../utils/constants';
import { HttpError } from '@wix/http-client';
import { AccountData, UserAccountsDataByEmailResponse } from '../types';
import {
  onSwitchContext,
  userClickedOnContinueWithEmail,
} from '@wix/bi-logger-hls2/v2';
import { loginPageClickOnLogInWithSso } from '@wix/bi-logger-identity-data/v2';
import { CAPTCHA_ACTIONS } from './captcha';
import { LoginEmailStepStore } from './loginEmailStep';
import { ISignupFlows, SIGNUP_FLOWS } from './signup';
import { LOGIN_FLOWS } from './login';
import { areAllUserAccountsBlocked, ERROR_CODES } from '../utils/errorHandler';
import { AUTH_TYPE } from '../components/BlockedAccount/authTypes';
import {
  findRequestedAccount,
  getInviteOrganizationId,
  getRequestedOrganizationId,
  organizationIdOf,
} from '../utils/requestedOrganization';

export type AuthHandlers = {
  MULTI: (params: { email: string }) => void;
  SINGLE: (params: { email: string; ssoUrl: string }) => void;
  LOGIN_PASSWORD: (params?: {
    flow?: string;
    withEmailCode?: boolean;
  }) => Promise<void>;
  SOCIAL_ONLY: (params: { socialConnectionsProviders: string[] }) => void;
  SIGNUP_PASSWORD: (params: { flow?: ISignupFlows }) => void | Promise<void>;
};

/**
 * The backend sends a login code only to a verified email that belongs to exactly
 * one user in the Wix default organization; anything else is refused.
 */
const isEmailCodeLoginEligible = (
  response?: UserAccountsDataByEmailResponse,
) => {
  const accounts = response?.accountsData ?? [];
  return (
    response?.emailVerified === true &&
    accounts.length === 1 &&
    organizationIdOf(accounts[0]) === WIX_DEFAULT_ORGANIZATION_ID
  );
};

/** The one account behind the email has no password to log in with. */
const isPasswordlessEmailCodeAccount = (
  response?: UserAccountsDataByEmailResponse,
) =>
  isEmailCodeLoginEligible(response) &&
  response?.accountsData?.[0]?.userAuthenticatorsData?.hasPassword === false;

export class EmailStepStore implements AuthStore {
  protected readonly rootStore: RootStore;
  private captchaAdded: boolean = false;
  public authHandlers: AuthHandlers;
  public emailField: FormField;
  public shouldRememberMe: boolean = true;
  public isLoading: boolean = false;
  public titleKey: string;
  public subtitleKey: string;
  public isEmailLoginMobileMode: boolean = false;
  public isFetchingByDefaultEmail: boolean = true;
  // The email the next email step opens with, instead of an empty field.
  private emailToFillIn = '';
  private hasFilledInAfterReload = false;
  private EMAIL_CHECK_INTERACTION_NAME = 'email-check';
  public loginEmailStepStore: LoginEmailStepStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    const { i18n, displayStore } = rootStore;
    this.titleKey =
      displayStore.preset.landingPage?.title?.key ?? 'emailStep.title';
    this.subtitleKey =
      displayStore.preset.landingPage?.subtitle?.key ?? 'emailStep.subTitle';
    makeObservable(this, {
      getContext: action.bound,
      submit: action.bound,
      onClickContinueWithSso: action.bound,
      onClickForgotEmail: action.bound,
      onClickForgotPassword: action.bound,
      shouldRememberMe: observable,
      isLoading: observable,
      isFetchingByDefaultEmail: observable,
      isEmailLoginMobileMode: observable,
    });
    const emailRules: Constraint[] = generateEmailFieldConstraints(
      i18n.t.bind(i18n),
    );
    this.emailField = new FormField(
      this.rootStore.userDataStore.email,
      emailRules,
    );

    if (this.rootStore.socialAuthStore.isUnsupportedAgentForSocialAuth) {
      this.isEmailLoginMobileMode = true;
    }
    this.loginEmailStepStore = new LoginEmailStepStore(rootStore);
    this.handlePostLoaded();
  }

  clear(): void {
    this.emailField.clear();
    this.isLoading = false;
    this.isFetchingByDefaultEmail = true;
  }

  onInitFields(): void {
    const { email } = this.rootStore.userDataStore;
    email && this.emailField.setValue(email);
  }

  /** Opens the next email step with this email filled in. */
  fillInEmail(email: string): void {
    this.emailToFillIn = email;
  }

  /**
   * The email step opened: an empty field, or the email asked for by
   * fillInEmail, or once after a reload of a later screen, the flow's email
   * before it. The URL's email, which is looked up on its own, comes first.
   */
  onEmailStepOpened(): void {
    this.emailField.clear();
    const email = this.emailToFillIn || this.emailLostByReload();
    if (email) {
      this.emailField.setValue(email);
      this.emailToFillIn = '';
    }
  }

  private emailLostByReload(): string {
    const { navigationStore, userDataStore } = this.rootStore;
    if (
      this.hasFilledInAfterReload ||
      userDataStore.email ||
      !navigationStore.wasReloadedAfterLookup()
    ) {
      return '';
    }
    this.hasFilledInAfterReload = true;
    return userDataStore.savedFlowBeforeReload?.email ?? '';
  }

  /**
   * After a reload of a later screen, looks up again the email whose account
   * was picked in the selector (or named by an IdP login), and continues with
   * that account as if picked again. A reload of the password form with no
   * pick looks its email up too, so one account opens its password form
   * again. Otherwise the lookup routes as the email step's does, to the
   * selector for several accounts. Other screens only get the email filled in:
   * a lookup there could send a new login code.
   */
  private restoreFlowAfterReload(): boolean {
    const { navigationStore, userDataStore } = this.rootStore;
    const saved = userDataStore.savedFlowBeforeReload;
    if (
      !saved ||
      userDataStore.email ||
      !navigationStore.wasReloadedAfterLookup() ||
      (!saved.organizationId &&
        !navigationStore.wasReloadedOn(ROUTES.LOGIN_PASSWORD))
    ) {
      return false;
    }
    this.fillInEmail(saved.email);
    this.emailField.setValue(saved.email);
    this.isLoading = true;
    this.handleEmailSubmit(saved.email, undefined, {
      restoredPickOrganizationId: saved.organizationId,
    })
      .catch(() => undefined)
      .finally(() => {
        this.isLoading = false;
        this.isFetchingByDefaultEmail = false;
      });
    return true;
  }

  presetSubtitleKey?: string | undefined;

  private async handlePostLoaded() {
    await when(() => this.rootStore.isLoaded);
    this.setAuthHandlers();
    if (this.restoreFlowAfterReload()) {
      return;
    }
    this.handleAutoSubmission();
  }

  public async attemptUsernamelessWebAuthnLogin() {
    await when(() => this.rootStore.isLoaded);

    try {
      const webAuthnResult =
        await this.rootStore.webauthnStore.authenticateWithUsernamelessWebAuthn();

      if (webAuthnResult.status !== 'success') {
        // Usernameless flow is optional - silent fallback on cancel or error
        return;
      }

      return this.rootStore.loginStore.login('', {
        publicKeyCredential: webAuthnResult.data.credential,
        webAuthnChallenge: webAuthnResult.data.challenge,
      });
    } catch (error) {}
  }

  protected setAuthHandlers() {
    const { ssoStore, socialAuthStore, signupStore, loginStore } =
      this.rootStore;
    const showLoginEmailStep = () =>
      this.loginEmailStepStore.showLoginEmailStep;
    const getLoginFlow = (flow?: string) => {
      if (flow === SIGNUP_FLOWS.DEFAULT) {
        return LOGIN_FLOWS.FROM_SIGNUP;
      }
      return flow;
    };
    this.authHandlers = {
      [AUTH_METHODS_BY_MAIL.MULTI]: ssoStore.navigateAccountSelector,
      [AUTH_METHODS_BY_MAIL.SINGLE]: ssoStore.redirectSsoUrl,
      [AUTH_METHODS_BY_MAIL.LOGIN_PASSWORD]: async (params?: {
        flow?: string;
        withEmailCode?: boolean;
      }) => {
        const loginFlow =
          getLoginFlow(params?.flow) ||
          (showLoginEmailStep()
            ? LOGIN_FLOWS.DEFAULT
            : LOGIN_FLOWS.FROM_SIGNUP);
        if (params?.withEmailCode) {
          return loginStore.onNavigateToLoginWithEmailCode(loginFlow);
        }
        return loginStore.onNavigateToLogin(loginFlow);
      },
      [AUTH_METHODS_BY_MAIL.SOCIAL_ONLY]: (params) =>
        socialAuthStore.setSocialProvidersAndNavigate({
          ...params,
          ...(!showLoginEmailStep() ? { showAlreadyExistsNote: true } : {}),
        }),
      [AUTH_METHODS_BY_MAIL.SIGNUP_PASSWORD]: (params?: {
        flow?: ISignupFlows;
      }) =>
        signupStore.onNavigateToSignup(
          params?.flow ||
            (showLoginEmailStep()
              ? SIGNUP_FLOWS.FROM_LOGIN
              : SIGNUP_FLOWS.DEFAULT),
        ),
    };
  }

  private async handleAutoSubmission() {
    const {
      navigationStore: { currentRoute, loginDialogContext },
      userDataStore,
    } = this.rootStore;
    const matchingDialogContext =
      !loginDialogContext ||
      [LOGIN_PAGE_CONTEXT, SIGNUP_PAGE_CONTEXT].some(
        (context) => context === loginDialogContext,
      );
    if (
      userDataStore.email &&
      currentRoute === ROUTES.EMAIL_STEP &&
      matchingDialogContext
    ) {
      await this.submit(true);
    }
    this.isFetchingByDefaultEmail = false;
  }

  /**
   * Records whether the looked-up email may log in with a code, and returns
   * it. Login offers the code only to an account without a password.
   */
  public recordEmailCodeEligibility(
    email: string,
    response?: UserAccountsDataByEmailResponse,
  ) {
    const eligible = isPasswordlessEmailCodeAccount(response);
    this.rootStore.loginStore.setCodeEligibleEmail(email, eligible);
    return eligible;
  }

  /**
   * @param organizationId the organization an IdP login names for this email,
   * which its merge continues in: it comes before an invite's and the account
   * selector's pick.
   */
  public getAuthMethodByAccountData(
    accounts: AccountData[],
    email: string,
    organizationId?: string,
  ): { method: IAUTH_METHODS_BY_MAIL; params?: { [key: string]: any } } {
    // Before the resolved account, and its pick, are dropped below.
    const requestedOrganizationId =
      organizationId ?? getRequestedOrganizationId(this.rootStore, email);
    // Every lookup re-resolves the account, so drop the previous account's
    // social connections first - otherwise a path that returns before an
    // account is resolved (multiple accounts, no account) would leave the
    // Facebook gate reading a stale account.
    this.rootStore.socialAuthStore.setAccountSocialConnectionsProviders([]);
    this.rootStore.userDataStore.clearResolvedAccount();
    // Should never happen since the server suppose to response with 404 in such a case
    if (!accounts && !this.rootStore.loginStore.shouldBlockEditorXSignup()) {
      return { method: AUTH_METHODS_BY_MAIL.SIGNUP_PASSWORD };
    }
    const { navigationStore } = this.rootStore;
    // An invite joins its organization: the email's account there logs in,
    // and an email with none there signs up for one, whatever other accounts
    // it has - unless this page doesn't sign up at all.
    const inviteOrganizationId = organizationId
      ? undefined
      : getInviteOrganizationId(this.rootStore);
    if (
      inviteOrganizationId &&
      !this.rootStore.loginStore.shouldBlockEditorXSignup() &&
      !accounts.some(
        (account) => organizationIdOf(account) === inviteOrganizationId,
      )
    ) {
      return { method: AUTH_METHODS_BY_MAIL.SIGNUP_PASSWORD };
    }
    const requested =
      accounts.length > 1 || inviteOrganizationId
        ? findRequestedAccount(accounts, {
            organizationId: inviteOrganizationId ?? requestedOrganizationId,
            parentHint: navigationStore.getQueryParam('parentHint'),
          })
        : undefined;
    /** Multiple accounts **/
    if (accounts.length > 1 && !requested) {
      return { method: AUTH_METHODS_BY_MAIL.MULTI };
    }
    /** Single account **/
    const account = requested ?? accounts[0];
    // Expose the resolved account's social connections so the following steps
    // can gate the Facebook SSO button (Symphony
    // `shouldSymphonyHideFacebookUnlessConnected` Conductor FT hides Facebook
    // unless this account actually has it).
    this.rootStore.socialAuthStore.setAccountSocialConnectionsProviders(
      account?.userAuthenticatorsData?.socialConnectionsProviders ?? [],
    );
    this.rootStore.userDataStore.setResolvedAccount(email, account, {
      namedByIdp:
        !!organizationId &&
        !!account &&
        organizationIdOf(account) === organizationId,
    });
    const ssoUrl = account?.accountSsoSettings?.ssoUrl;
    const isSsoMandatory =
      ssoUrl &&
      (account?.accountSsoSettings?.isSsoMandatory ||
        !account?.userAuthenticatorsData?.hasPassword);
    if (isSsoMandatory) {
      return {
        method: AUTH_METHODS_BY_MAIL.SINGLE,
        params: { ssoUrl },
      };
    }
    if (this.rootStore.socialAuthStore.shouldProceedToSocialAuthOnly(account)) {
      return {
        method: AUTH_METHODS_BY_MAIL.SOCIAL_ONLY,
        params: {
          socialConnectionsProviders:
            account?.userAuthenticatorsData?.socialConnectionsProviders,
          ssoUrl,
        },
      };
    }
    return { method: AUTH_METHODS_BY_MAIL.LOGIN_PASSWORD };
  }

  /**
   * @param organizationId the organization an IdP login names for this email,
   * for its lookup only.
   */
  public async submit(
    isDefaultEmail: boolean = false,
    { organizationId }: { organizationId?: string } = {},
  ) {
    !isDefaultEmail &&
      this.rootStore.biLogger.report(userClickedOnContinueWithEmail({}));

    // Re-run the format rules only: a server error from the previous submit
    // would otherwise block every retry until the email is edited.
    this.emailField.triggerValidator();
    if (!this.emailField.isValid || this.isLoading) {
      this.emailField.markFieldAsDirty();
      return;
    }
    this.isLoading = true;
    this.handleEmailSubmit(this.emailField.value, undefined, { organizationId })
      .catch((e) => {
        if (e.message === ERROR_CODES.ACCOUNT_NOT_FOUND_AND_SIGNUP_DISABLED) {
          this.emailField.addError(
            this.rootStore.i18n.t('account.not.found.error'),
          );
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  /**
   * @param organizationId the organization an IdP login names for this email.
   * @param restoredPickOrganizationId the organization of the account picked
   * before a reload; found among the accounts, it is picked again.
   */
  public async handleEmailSubmit(
    email: string,
    flow?: string,
    {
      organizationId,
      restoredPickOrganizationId,
    }: { organizationId?: string; restoredPickOrganizationId?: string } = {},
  ) {
    this.rootStore.webauthnStore.abortWebAuthnAuthentication();

    this.rootStore.panorama.startTransaction(this.EMAIL_CHECK_INTERACTION_NAME);
    const recaptchaParams =
      await this.rootStore.captchaStore.handleRecaptchaExecution({
        captchaAdded: this.captchaAdded,
        action: CAPTCHA_ACTIONS.GET_USER_ACCOUNTS,
      });

    this.rootStore.userDataStore.setEmail(email);
    this.rootStore.socialAuthStore.setAccountSocialConnectionsProviders([]);
    const { apiStore } = this.rootStore;

    try {
      const response = await apiStore.getUserAccounts({
        email,
        ...recaptchaParams,
      });

      const authType = this.getAuthMethodByAccountData(
        response?.accountsData!,
        email,
        organizationId,
      );
      const withEmailCode = this.recordEmailCodeEligibility(email, response);
      this.rootStore.panorama.finishTransaction(
        this.EMAIL_CHECK_INTERACTION_NAME,
      );

      const shouldNavigateToBlockedAccount = this.rootStore.experiments.enabled(
        'specs.login.ShouldNavigateToBlockedAccount',
      );
      if (
        shouldNavigateToBlockedAccount &&
        response.accountsData &&
        areAllUserAccountsBlocked(response.accountsData)
      ) {
        return this.rootStore.navigationStore.navigateToBlockedAccount({
          refferal_info: AUTH_TYPE.EMAIL_AND_PASSWORD,
        });
      }

      const restoredPick =
        restoredPickOrganizationId &&
        response?.accountsData?.find(
          (account) => organizationIdOf(account) === restoredPickOrganizationId,
        );
      if (restoredPick) {
        return await this.rootStore.selectAccountStore.onAccountSelected({
          ...restoredPick,
          loggedInUserData: response.loggedInUserData,
        });
      }

      if (
        authType.method === AUTH_METHODS_BY_MAIL.LOGIN_PASSWORD &&
        (await this.rootStore.webauthnStore.tryPasskeyLogin(email))
      ) {
        return;
      }

      await this.authHandlers[authType.method]({
        email,
        flow,
        withEmailCode,
        ...(authType.params ?? {}),
      } as any);
    } catch (error: any) {
      this.isLoading = false;
      const serverErrorCode = (
        error as HttpError
      )?.response?.data.errorCode?.toString();
      const emailDoesNotExist = this.isUserEmailDoesNotExist(error);
      const isValidInteraction =
        emailDoesNotExist ||
        this.rootStore.captchaStore.isCaptchaServerError(serverErrorCode);
      if (isValidInteraction) {
        this.rootStore.panorama.finishTransaction(
          this.EMAIL_CHECK_INTERACTION_NAME,
        );
      }
      if (emailDoesNotExist) {
        if (this.rootStore.loginStore.shouldBlockEditorXSignup()) {
          throw new Error(ERROR_CODES.ACCOUNT_NOT_FOUND_AND_SIGNUP_DISABLED);
        } else {
          this.isLoading = true;
          try {
            await this.authHandlers[AUTH_METHODS_BY_MAIL.SIGNUP_PASSWORD]({
              flow: flow as ISignupFlows,
            });
          } finally {
            this.isLoading = false;
          }
        }
      }
      this.captchaAdded =
        this.rootStore.captchaStore.createOrResetCaptchaIfNeeded(
          serverErrorCode,
          this.captchaAdded,
        );
    }
  }

  isUserEmailDoesNotExist(error) {
    return (error as HttpError)?.response?.status?.toString() === '404';
  }

  getAccountRecoveryLink(mode: 'email' | 'password') {
    // Recovery renders unbranded and mails an unbranded reset link unless it is
    // told which vertical the user came from — nothing else in the hand-off
    // carries it. See RECOVERY_FORWARDED_USER_TYPES for why this is a list.
    const accountRecoveryParams = forgotPasswordParams({
      sessionId: this.rootStore.sessionId.get(),
      from: window.location.href,
      userType: this.rootStore.userType,
    });

    if (mode === 'password') {
      return `${ACCOUNT_RECOVERY_FORGOT_PASSWORD_URL}?${accountRecoveryParams.toString()}`;
    }
    if (mode === 'email') {
      // The questionnaire answers "which email did I sign up with?" by looking up
      // the Wix site the user owns. These verticals own none, so it cannot answer
      // it — sending them there only brands a dead end.
      const supportUrl = getVerticalSupportUrl(this.rootStore.userType);
      if (supportUrl) {
        return supportUrl;
      }

      return `${NEW_ACCOUNT_RECOVERY_URL}?${accountRecoveryParams.toString()}`;
    }

    return `/account-recovery?${accountRecoveryParams.toString()}`;
  }

  public getSsoWithEmailLink() {
    return ROUTES.SSO_ENTER_EMAIL;
  }

  onClickForgotEmail() {
    this.reportRecoverySwitch('forgot-email');
  }

  /** The landing step offers this instead of forgot-email for some presets. */
  onClickForgotPassword() {
    this.reportRecoverySwitch('forgot-password');
  }

  private reportRecoverySwitch(context: 'forgot-email' | 'forgot-password') {
    this.rootStore.biLogger.report(
      onSwitchContext({
        context,
        referrer: this.rootStore.navigationStore.originContext,
      }),
    );
  }

  onClickContinueWithSso() {
    this.rootStore.biLogger.report(loginPageClickOnLogInWithSso({}));
    this.rootStore.navigationStore.navigate(this.getSsoWithEmailLink());
  }

  getContext() {
    return this.loginEmailStepStore.showLoginEmailStep
      ? LOGIN_PAGE_CONTEXT
      : SIGNUP_PAGE_CONTEXT;
  }
}
